import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBusinessAreas } from '../../../../../services/taskNotificationsService';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configTasksI18n } from './i18n';

export default function TaskCreateEditModal({
  DesignSystemComponents,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
  serviceOptions = [],
  documentOptions = [],
  taskTypeOptions = [],
  loadingServices = false,
  isLoadingSaving = false,
}) {
  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configTasksI18n });

  const { Modal, Input, Dropdown, ComboBox } = DesignSystemComponents;
  const [areaOptions, setAreaOptions] = useState([]);
  const isEdit = mode === 'edit';

  const [local, setLocal] = useState({
    id: null,
    order: '',
    code: '',
    description: '',
    serviceId: null,
    serviceName: '',
    documentId: null,
    documentName: '',
    taskTypeId: null,
    taskTypeName: '',
    startMessage: '',
    laborCost: 0,
    endMessage: '',
    businessAreaId: null,
  });

  useEffect(() => {
    configTasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  useEffect(() => {
    const loadAll = async () => {
      const areas = await getBusinessAreas();
      setAreaOptions(areas.map((area) => ({ label: area.name, value: area.id })));
    };

    if (show) {
      setLocal({
        id: value?.id ?? null,
        order: value?.order ?? '',
        code: value?.code ?? '',
        description: value?.description ?? '',
        serviceId: value?.serviceId ?? null,
        serviceName: value?.serviceName ?? '',
        documentId: value?.documentId ?? null,
        documentName: value?.documentName ?? '',
        taskTypeId: value?.taskTypeId ?? null,
        taskTypeName: value?.taskTypeName ?? '',
        startMessage: value?.startMessage ?? '',
        laborCost: value?.laborCost ?? 0,
        endMessage: value?.endMessage ?? '',
        businessAreaId: value?.businessAreaId ?? null,
      });
    }
    loadAll();
  }, [show, value]);

  const isValid = useMemo(() => {
    const ord = String(local.order).trim();
    const orderValid = ord !== '' && !Number.isNaN(Number(ord));
    const codeValid = local.code.trim().length > 0;
    const nameValid = local.description.trim().length > 0;
    const serviceValid = local.serviceId != null && local.serviceId > 0;
    const taskTypeValid = local.taskTypeId != null && local.taskTypeId > 0;

    return (
      orderValid && codeValid && nameValid && serviceValid && taskTypeValid
    );
  }, [local]);

  const setField = (key) => (eventOrValue) => {
    const nextValue = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleSave = () => {
    if (!isValid) return;
    onChange?.(local);
    onSave?.(local);
  };

  const serviceOptionFrom = (val) =>
    serviceOptions.find((option) => option.value === val) ?? null;

  const documentOptionFrom = (val, name) => {
    if (val !== null && val !== undefined) {
      const normalizedVal = Number(val);
      return (
        documentOptions.find((option) => {
          const normalizedOpt = Number(option.value);
          if (!Number.isNaN(normalizedVal) && !Number.isNaN(normalizedOpt)) {
            return normalizedOpt === normalizedVal;
          }
          return option.value === val;
        }) ?? null
      );
    }
    if (!name) return null;
    return documentOptions.find((option) => option.label === name) ?? null;
  };

  const taskTypeOptionFrom = (val) =>
    taskTypeOptions.find((option) => option.value === val) ?? null;

  const handleChange = (field) => (opt) => {
    setLocal((prev) => ({
      ...prev,
      [field]: opt?.value ?? opt,
    }));
  };

  if (!Modal || !Input) return null;

  return (
    <Modal
      title={
        isEdit
          ? t('config_tasks_modal_title_edit')
          : t('config_tasks_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit
          ? t('config_tasks_modal_primary_edit')
          : t('config_tasks_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || isLoadingSaving}
      secondaryBtnTxt={t('config_tasks_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='900px'
    >
      <div className='row g-3'>
        <div className='col-12 col-md-3'>
          <Input
            label={t('config_tasks_modal_order_label')}
            required
            placeholder={t('config_tasks_modal_order_placeholder')}
            value={local.order}
            onChange={setField('order')}
            inputMode='numeric'
            error={
              local.order && Number.isNaN(Number(local.order))
                ? t('config_tasks_modal_order_error')
                : ''
            }
          />
        </div>
        <div className='col-12 col-md-4'>
          <Input
            label={t('config_tasks_modal_code_label')}
            required
            placeholder={t('config_tasks_modal_code_placeholder')}
            value={local.code}
            onChange={setField('code')}
            error={
              local.code && !local.code.trim()
                ? t('config_tasks_modal_code_required')
                : ''
            }
          />
        </div>
        <div className='col-12 col-md-5'>
          <label className='form-label'>
            {t('config_tasks_modal_service_label')} <span className='text-danger'>*</span>
          </label>
          {Dropdown ? (
            <Dropdown
              options={serviceOptions}
              value={serviceOptionFrom(local.serviceId)}
              onChange={(opt) => {
                setLocal((state) => ({
                  ...state,
                  serviceId: opt?.value || null,
                  serviceName: opt?.label || '',
                }));
              }}
              placeholder={
                loadingServices
                  ? t('config_tasks_modal_loading')
                  : t('config_tasks_modal_service_placeholder')
              }
              isDisabled={loadingServices}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.serviceId || ''}
              onChange={(event) => {
                const selectedId = parseInt(event.target.value, 10);
                const selectedOption = serviceOptions.find(
                  (option) => option.value === selectedId
                );
                setLocal((state) => ({
                  ...state,
                  serviceId: selectedId || null,
                  serviceName: selectedOption?.label || '',
                }));
              }}
              disabled={loadingServices}
            >
              <option value=''>
                {loadingServices
                  ? t('config_tasks_modal_loading')
                  : t('config_tasks_modal_service_placeholder')}
              </option>
              {serviceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className='col-12 col-md-4'>
          <label className='form-label'>{t('config_tasks_modal_document_label')}</label>
          {Dropdown ? (
            <Dropdown
              options={documentOptions}
              value={documentOptionFrom(local.documentId, local.documentName)}
              onChange={(opt) =>
                setLocal((state) => ({
                  ...state,
                  documentId: opt?.value || null,
                  documentName: opt?.label || '',
                }))
              }
              placeholder={t('config_tasks_modal_document_placeholder')}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.documentId || ''}
              onChange={(event) => {
                const id = event.target.value ? parseInt(event.target.value, 10) : null;
                setLocal((state) => ({ ...state, documentId: id }));
              }}
            >
              <option value=''>{t('config_tasks_modal_document_placeholder')}</option>
              {documentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className='col-12 col-md-4'>
          <label className='form-label'>
            {t('config_tasks_modal_type_label')} <span className='text-danger'>*</span>
          </label>
          {Dropdown ? (
            <Dropdown
              options={taskTypeOptions}
              value={taskTypeOptionFrom(local.taskTypeId)}
              onChange={(opt) =>
                setLocal((prev) => ({
                  ...prev,
                  taskTypeId: opt?.value || null,
                  taskTypeName: opt?.label || '',
                }))
              }
              placeholder={
                loadingServices
                  ? t('config_tasks_modal_loading')
                  : t('config_tasks_modal_type_placeholder')
              }
              isDisabled={loadingServices}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.taskTypeId || ''}
              onChange={(event) =>
                setLocal((prev) => ({
                  ...prev,
                  taskTypeId: event.target.value
                    ? parseInt(event.target.value, 10)
                    : null,
                }))
              }
              disabled={loadingServices}
            >
              <option value=''>
                {loadingServices
                  ? t('config_tasks_modal_loading')
                  : t('config_tasks_modal_type_placeholder')}
              </option>
              {taskTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className='col-12 col-md-4'>
          <Input
            label={t('config_tasks_modal_weight_label')}
            helperText={t('config_tasks_modal_weight_helper')}
            required
            placeholder={t('config_tasks_modal_weight_placeholder')}
            value={local.laborCost || ''}
            onChange={(event) =>
              setLocal((prev) => ({
                ...prev,
                laborCost: event.target.value.replace(/\D/g, ''),
              }))
            }
            inputMode='numeric'
          />
        </div>

        <div className='col-12 col-md-6'>
          <ComboBox
            label={t('config_tasks_modal_area_label')}
            placeholder={t('config_tasks_modal_area_placeholder')}
            options={areaOptions}
            value={
              areaOptions.find((option) => option.value === local.businessAreaId) ||
              null
            }
            onChange={handleChange('businessAreaId')}
            width='100%'
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('config_tasks_modal_description_label')}
            required
            placeholder={t('config_tasks_modal_description_placeholder')}
            value={local.description}
            onChange={setField('description')}
            error={
              local.description && !local.description.trim()
                ? t('config_tasks_modal_description_required')
                : ''
            }
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>{t('config_tasks_modal_start_message_label')}</label>
          <textarea
            className='form-control'
            rows={6}
            placeholder={t('config_tasks_modal_start_message_placeholder')}
            value={local.startMessage}
            onChange={setField('startMessage')}
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>{t('config_tasks_modal_end_message_label')}</label>
          <textarea
            className='form-control'
            rows={6}
            placeholder={t('config_tasks_modal_end_message_placeholder')}
            value={local.endMessage}
            onChange={setField('endMessage')}
          />
        </div>
      </div>
    </Modal>
  );
}
